import React, { forwardRef } from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { cva, type VariantProps } from 'class-variance-authority';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { cn } from '../theme';

// ============================================================================
// TOAST PROVIDER
// ============================================================================

const ToastProvider = ToastPrimitive.Provider;

// ============================================================================
// TOAST VIEWPORT
// ============================================================================

const ToastViewport = forwardRef<
  React.ElementRef<typeof ToastPrimitive.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Viewport
    ref={ref}
    className={cn(
      'fixed top-0 right-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4',
      'sm:top-auto sm:bottom-0 sm:right-0 sm:flex-col md:max-w-[420px]',
      className
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitive.Viewport.displayName;

// ============================================================================
// TOAST VARIANTS
// ============================================================================

const toastVariants = cva(
  [
    'group pointer-events-auto relative flex w-full items-center justify-between space-x-4',
    'overflow-hidden rounded-lg border p-4 shadow-medium',
    'transition-all',
    'data-[swipe=cancel]:translate-x-0',
    'data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)]',
    'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]',
    'data-[swipe=move]:transition-none',
    'data-[state=open]:animate-in data-[state=closed]:animate-out',
    'data-[state=closed]:fade-out-80',
    'data-[state=closed]:slide-out-to-right-full',
    'data-[state=open]:slide-in-from-top-full',
    'data-[state=open]:sm:slide-in-from-bottom-full',
  ],
  {
    variants: {
      variant: {
        default: 'border-warm-gray/20 bg-white text-navy',
        success: 'border-success/20 bg-success/5 text-success',
        error: 'border-red-500/20 bg-red-500/5 text-red-600',
        warning: 'border-terracotta/20 bg-terracotta/5 text-terracotta',
        info: 'border-blue-500/20 bg-blue-500/5 text-blue-600',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

// ============================================================================
// TOAST
// ============================================================================

export interface ToastProps
  extends React.ComponentPropsWithoutRef<typeof ToastPrimitive.Root>,
    VariantProps<typeof toastVariants> {}

const Toast = forwardRef<
  React.ElementRef<typeof ToastPrimitive.Root>,
  ToastProps
>(({ className, variant, ...props }, ref) => {
  return (
    <ToastPrimitive.Root
      ref={ref}
      className={cn(toastVariants({ variant }), className)}
      {...props}
    />
  );
});
Toast.displayName = ToastPrimitive.Root.displayName;

// ============================================================================
// TOAST ACTION
// ============================================================================

const ToastAction = forwardRef<
  React.ElementRef<typeof ToastPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Action
    ref={ref}
    className={cn(
      'inline-flex h-8 shrink-0 items-center justify-center rounded-md border border-warm-gray/20',
      'bg-transparent px-3 text-sm font-medium',
      'transition-colors',
      'hover:bg-cream',
      'focus:outline-none focus:ring-2 focus:ring-sage focus:ring-offset-2',
      'disabled:pointer-events-none disabled:opacity-50',
      'group-[.success]:border-success/30 group-[.success]:hover:bg-success/10',
      'group-[.error]:border-red-500/30 group-[.error]:hover:bg-red-500/10',
      className
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitive.Action.displayName;

// ============================================================================
// TOAST CLOSE
// ============================================================================

const ToastClose = forwardRef<
  React.ElementRef<typeof ToastPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Close
    ref={ref}
    className={cn(
      'absolute right-2 top-2 rounded-md p-1',
      'text-warm-gray/50 hover:text-warm-gray',
      'transition-colors',
      'focus:outline-none focus:ring-2 focus:ring-sage',
      className
    )}
    toast-close=""
    {...props}
  >
    <X className="h-4 w-4" />
  </ToastPrimitive.Close>
));
ToastClose.displayName = ToastPrimitive.Close.displayName;

// ============================================================================
// TOAST TITLE
// ============================================================================

const ToastTitle = forwardRef<
  React.ElementRef<typeof ToastPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Title
    ref={ref}
    className={cn('text-sm font-semibold', className)}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitive.Title.displayName;

// ============================================================================
// TOAST DESCRIPTION
// ============================================================================

const ToastDescription = forwardRef<
  React.ElementRef<typeof ToastPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Description
    ref={ref}
    className={cn('text-sm opacity-90', className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitive.Description.displayName;

// ============================================================================
// TOAST ICON
// ============================================================================

interface ToastIconProps {
  variant?: 'default' | 'success' | 'error' | 'warning' | 'info';
}

const ToastIcon: React.FC<ToastIconProps> = ({ variant = 'default' }) => {
  const icons = {
    default: null,
    success: <CheckCircle className="h-5 w-5" />,
    error: <AlertCircle className="h-5 w-5" />,
    warning: <AlertTriangle className="h-5 w-5" />,
    info: <Info className="h-5 w-5" />,
  };

  return icons[variant];
};

// ============================================================================
// EXPORTS
// ============================================================================

export {
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
  ToastIcon,
  toastVariants,
};

export type { ToastProps };
